import React, { useState, useRef } from 'react';
import { createWorker } from 'tesseract.js';

interface OCRComponentProps {
  onTextExtracted?: (text: string) => void;
}

const OCRComponent: React.FC<OCRComponentProps> = ({ onTextExtracted }) => {
  const [image, setImage] = useState<string | null>(null);
  const [text, setText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target?.result as string);
        setText('');
        // 自动触发OCR识别
        if (e.target?.result) {
          setTimeout(() => extractText(), 100);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // 自动识别功能
  const handleAutoOCR = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setProgress(0);
    setText('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      // 使用腾讯云OCR优先
      if (process.env.REACT_APP_USE_TENCENT_OCR === 'true') {
        const response = await fetch('/api/payment-screenshots/ocr-scan-auto', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          setText(result.text || '');
          setImage(URL.createObjectURL(file));
        } else {
          throw new Error('腾讯云OCR失败');
        }
      } else {
        // 使用本地Tesseract OCR
        const reader = new FileReader();
        reader.onload = async (e) => {
          const imageData = e.target?.result as string;
          setImage(imageData);
          await extractTextWithTesseract();
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('自动OCR识别错误:', error);
      setText('Error: 自动识别失败，请重试');
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const extractText = async () => {
    if (!image) return;

    setIsProcessing(true);
    setProgress(0);
    setText('');

    try {
      // 尝试使用腾讯云OCR（如果可用）
      if (process.env.REACT_APP_USE_TENCENT_OCR === 'true') {
        await extractTextWithTencentCloud();
      } else {
        await extractTextWithTesseract();
      }
    } catch (error) {
      console.error('OCR Error:', error);
      // 如果腾讯云OCR失败，回退到本地OCR
      if (process.env.REACT_APP_USE_TENCENT_OCR === 'true') {
        console.log('腾讯云OCR失败，回退到本地OCR...');
        await extractTextWithTesseract();
      } else {
        setText('Error: Failed to extract text from image');
      }
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const extractTextWithTesseract = async () => {
    if (!image) return;
    
    const worker = await createWorker('eng+chi_sim', 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          setProgress(m.progress * 100);
        }
      },
    });

    const { data: { text } } = await worker.recognize(image);
    setText(text);
    onTextExtracted?.(text);
    await worker.terminate();
  };

  const extractTextWithTencentCloud = async () => {
    if (!image) return;
    
    // 将base64图片转换为文件
    const response = await fetch(image);
    const blob = await response.blob();
    const file = new File([blob], 'ocr-image.jpg', { type: 'image/jpeg' });
    
    const formData = new FormData();
    formData.append('image', file);
    
    const apiResponse = await fetch('/api/payment-screenshots/ocr-scan', {
      method: 'POST',
      body: formData,
    });
    
    if (!apiResponse.ok) {
      throw new Error('腾讯云OCR请求失败');
    }
    
    const result = await apiResponse.json();
    setText(result.ocrText);
    onTextExtracted?.(result.ocrText);
  };

  const clearImage = () => {
    setImage(null);
    setText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <h2>图片文字识别 (OCR)</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleAutoOCR}
          style={{ marginBottom: '10px' }}
        />
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
          选择图片后自动开始识别
        </p>
      </div>

      {image && (
        <div style={{ marginBottom: '20px' }}>
          <img 
            src={image} 
            alt="Uploaded" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '400px', 
              border: '1px solid #ddd',
              borderRadius: '4px'
            }} 
          />
          <div style={{ marginTop: '10px' }}>
            <button 
              onClick={extractText}
              disabled={isProcessing}
              style={{
                padding: '8px 16px',
                marginRight: '10px',
                backgroundColor: isProcessing ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              {isProcessing ? '处理中...' : '提取文字'}
            </button>
            <button 
              onClick={clearImage}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              清除图片
            </button>
          </div>
        </div>
      )}

      {isProcessing && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            width: '100%', 
            height: '20px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <p style={{ textAlign: 'center', marginTop: '5px' }}>
            处理进度: {Math.round(progress)}%
          </p>
        </div>
      )}

      {text && (
        <div>
          <h3>识别结果:</h3>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
          <button
            onClick={() => navigator.clipboard.writeText(text)}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            复制到剪贴板
          </button>
        </div>
      )}
    </div>
  );
};

export default OCRComponent;